/**
 * Created by rhinov20e on 8/26/2016.
 *
 * Node to show that an object/entity can be resized
 */

import { ArrangementObject, Entity, math, SavaneMath, SceneConstants } from "@rhinov/savane-js";
import { PlanManager } from "../../../../cocosPlan";

declare var cc: any;

export class ResizeNode extends cc.DrawNode {
    constructor(entity: Entity, forFrontView: boolean, scale: number) {
         // Call parent constructor
        super();

        // Save savane entity showing this resize node
        this.entity = entity;

        // Compute graphical size from savane entity (divided by 10)
        var cocosLength;
        var cocosWidth;

        if (this.entity.realWidth !== undefined) {
            cocosLength = this.entity.realWidth / 10;   
        } else if (this.entity.width !== undefined) {
            cocosLength = this.entity.width / 10;
        } else {
            cocosLength = this.entity.diameter / 10;
        }

        if (this.entity.realLength !== undefined) {
            cocosWidth = this.entity.realLength / 10;
        } else if (this.entity.length !== undefined) {
            cocosWidth = this.entity.length / 10;
        } else {
            cocosWidth = this.entity.diameter / 10;
        }

        // If this is for front view cocos length becomes entity height (and not entity width)
        this.forFrontView = forFrontView;
        if (this.forFrontView === true) {
            if (this.entity.joineryType === SceneConstants.JoineryType.velux) {
                cocosLength = this.entity.getVerticalHeight() * scale;
            }
            else {
                cocosLength = this.entity.height * scale;
            }
            cocosWidth = this.entity.length * scale;
        }

        // Colors for the circles
        var yellow = new cc.color('#F5D231');
        var grey = new cc.Color(66, 80, 94, 255);
        var radius = 4;

        // This will allow us to put horizontal or vertical resize only, as default they are both displayed
        this.allowWidth = true;
        this.allowHeight = true;

        // If the entity is an arrangement object we allow horizontal and vertical only if the object is stretchable in the according direction
        if (entity.entityType === SceneConstants.EntityType.ArrangementObject) {
            // For arrangeent objects we do not resize by default
            this.allowWidth = false;
            this.allowHeight = false;

            // We only allow resize if the object stretchability exist
            if ((entity as ArrangementObject).stretchability) {
                // And we allow horizontal resize if the stretchability x exist and is true
                if ((entity as ArrangementObject).stretchability.x !== undefined) {
                    if ((entity as ArrangementObject).stretchability.x.isStretchable) {
                        this.allowWidth = true;
                    }
                }

                // And we allow vertical resize if the stretchability y exist and is true
                if ((entity as ArrangementObject).stretchability.y !== undefined) {
                    if ((entity as ArrangementObject).stretchability.y.isStretchable) {
                        this.allowHeight = true;
                    }
                }
            }
        }

        // Create 4 draw nodes into which we will display filled circles one for each edge of the element of resize (left, right, top and bottom)

        // Vertical resize
        if (this.allowHeight) {
            var resize0 = new cc.DrawNode();
            resize0.setPosition(0, cocosLength * 0.5);
            resize0.setContentSize(new cc.size(12, 12));
            resize0.setName('top');
            resize0.drawDot(new cc.p(0, 0), radius + 2, grey);
            resize0.drawDot(new cc.p(0, 0), radius, yellow);
            this.addChild(resize0, 4);

            var resize1 = new cc.DrawNode();
            resize1.setPosition(0, -(0.5 * cocosLength));
            resize1.setContentSize(new cc.size(12, 12));
            resize1.setName('bot');
            resize1.drawDot(new cc.p(0, 0), radius + 2, grey);
            resize1.drawDot(new cc.p(0, 0), radius, yellow);
            this.addChild(resize1, 4);
        }

        // Horizontal resize
        if (this.allowWidth) {
            var resize2 = new cc.DrawNode();
            resize2.setPosition(cocosWidth * 0.5, 0);
            resize2.setContentSize(new cc.size(12, 12));
            resize2.setName('right');
            resize2.drawDot(new cc.p(0, 0), radius + 2, grey);
            resize2.drawDot(new cc.p(0, 0), radius, yellow);
            this.addChild(resize2, 4);

            var resize3 = new cc.DrawNode();
            resize3.setPosition(-(0.5 * cocosWidth), 0);
            resize3.setContentSize(new cc.size(12, 12));
            resize3.setName('left');
            resize3.drawDot(new cc.p(0, 0), radius + 2, grey);
            resize3.drawDot(new cc.p(0, 0), radius, yellow);
            this.addChild(resize3, 4);
        }
    }

    // Node update, prepare for next frame
    update(scale: number) {
        // Compute graphical size from savane entity (divided by 10)
        var cocosLength;
        var cocosWidth;

        if (this.entity.realWidth !== undefined) {
            cocosLength = this.entity.realWidth / 10;   
        } else if (this.entity.width !== undefined) {
            cocosLength = this.entity.width / 10;
        } else {
            cocosLength = this.entity.diameter / 10;
        }

        if (this.entity.realLength !== undefined) {
            cocosWidth = this.entity.realLength / 10;
        } else if (this.entity.length !== undefined) {
            cocosWidth = this.entity.length / 10;
        } else {
            cocosWidth = this.entity.diameter / 10;
        }

        // If this is for front view cocos length becomes entity height (and not entity width)
        if (this.forFrontView === true) {
            if (this.entity.joineryType === SceneConstants.JoineryType.velux) {
                cocosLength = this.entity.getVerticalHeight() * scale;
            }
            else {
                cocosLength = this.entity.height * scale;
            }

            cocosWidth = this.entity.length * scale;
        }

        // Colors for the circles
        var yellow = new cc.color('#F5D231');
        var grey = new cc.Color(66, 80, 94, 255);
        var radius = 4;

        if (this.entity.locked) {
            yellow = new cc.color(255, 125, 141, 255)
        }

        // Get sub nodes from current node
        // Set their positions
        if (this.allowWidth) {
            var left = this.getChildByName('left');
            var right = this.getChildByName('right');
            right.setPosition(cocosWidth * 0.5, 0);
            left.setPosition(cocosWidth * -0.5, 0);

            right.clear();
            right.drawDot(new cc.p(0, 0), radius + 2, grey);
            right.drawDot(new cc.p(0, 0), radius, yellow);
            left.clear();
            left.drawDot(new cc.p(0, 0), radius + 2, grey);
            left.drawDot(new cc.p(0, 0), radius, yellow);
        }

        if (this.allowHeight) {
            var top = this.getChildByName('top');
            var bot = this.getChildByName('bot');
            bot.setPosition(0, cocosLength * -0.5);
            top.setPosition(0, cocosLength * 0.5);

            top.clear();
            top.drawDot(new cc.p(0, 0), radius + 2, grey);
            top.drawDot(new cc.p(0, 0), radius, yellow);
            bot.clear();
            bot.drawDot(new cc.p(0, 0), radius + 2, grey);
            bot.drawDot(new cc.p(0, 0), radius, yellow);
        }
    }

    // Says weither or not the mouse click with position "position" will allow a resize operation
    toResize(position: math.vec3, scale: number) {
        // Do this only if the node is visible
        if (this.isVisible()) {
            var rotation = this.getParent().getRotation() * 0.0174533;

            var locationInNode = new cc.p((this.entity.position[0] - position[0]) / 10, (this.entity.position[1] - position[1]) / 10);
            if (this.forFrontView === true) {
                var entityFloorHeight;
                var entityHeight;

                if (this.entity.joineryType === SceneConstants.JoineryType.velux) {
                    entityHeight = this.entity.getVerticalHeight();
                    entityFloorHeight = this.entity.position[2] - (entityHeight / 2);
                }
                else {
                    entityFloorHeight = this.entity.floorHeight;
                    entityHeight = this.entity.height;
                }

                var wall = PlanManager.getInstance().frontViewLayer.currentWall!;
                var bboxcut = wall.boundingBoxCut;
                var realLength = PlanManager.getInstance().frontViewLayer.computeWallLength();

                var point1 = math.vec3.clone(this.entity.position);
                var point2 = math.vec3.create();
                point1[2] = wall.begin[2];
                math.vec3.set(point2,
                    point1[0] + wall.wallDirection[1] * 100,
                    point1[1] + wall.wallDirection[0] * 100,
                    wall.begin[2])

                var projOnWall = SavaneMath.getCrossPoint(point1, point2, wall.begin, wall.end);
                if (projOnWall) {
                    if (PlanManager.getInstance().frontViewLayer.isDirectSide) {
                        if (this.entity.isJoineryEntity() || this.entity.isTechnicalElementEntity()) {
                            var d = math.vec2.create();
                            math.vec2.subtract(d, projOnWall, bboxcut[1]);
                            locationInNode = new cc.p((math.vec2.length(d) - position[0]) * scale,
                                (entityFloorHeight + entityHeight / 2 - position[2]) * scale);
                        }
                    }
                    else {
                        if (this.entity.isJoineryEntity() || this.entity.isTechnicalElementEntity()) {
                            var d = math.vec2.create();
                            math.vec2.subtract(d, projOnWall, bboxcut[0]);
                            locationInNode = new cc.p(((realLength - math.vec2.length(d)) - position[0]) * scale,
                                (entityFloorHeight + entityHeight / 2 - position[2]) * scale);
                        }
                    }
                }
            }

            var locationInNodeR = new cc.p(0, 0);
            // Compute location into the node. The 6 at the end is 12/2, half size of the draw node (!= cc sprites where this would be unecessary)
            locationInNodeR.x = locationInNode.x * Math.cos(rotation) - locationInNode.y * Math.sin(rotation) + 6;
            locationInNodeR.y = locationInNode.x * Math.sin(rotation) + locationInNode.y * Math.cos(rotation) + 6;

            // Check if touch is inside button sprite
            var left = this.getChildByName('left');
            var right = this.getChildByName('right');
            var top = this.getChildByName('top');
            var bot = this.getChildByName('bot');
            if (this.allowWidth) {
                var contained = (cc.rectContainsPoint(left.getBoundingBox(), locationInNodeR) || cc.rectContainsPoint(right.getBoundingBox(), locationInNodeR));
                if (contained) {
                    if (this.allowHeight) {
                        if (cc.rectContainsPoint(top.getBoundingBox(), locationInNodeR) || cc.rectContainsPoint(bot.getBoundingBox(), locationInNodeR)) {
                            // Container in both
                            if (Math.abs(locationInNodeR.y) > Math.abs(locationInNodeR.x)) {
                                return this.forFrontView === true ? 'height' : 'width';
                            }
                        }
                    }
                    return 'length';
                }
            }

            if (this.allowHeight) {
                contained = cc.rectContainsPoint(top.getBoundingBox(), locationInNodeR) || cc.rectContainsPoint(bot.getBoundingBox(), locationInNodeR);
                if (contained) {
                    return this.forFrontView === true ? 'height' : 'width';
                }
                return false;
            }
        }
        return false;
    }
}
